import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  api,
  type PitchRule,
  type RestRule,
  type Season,
} from '../../services/api';
import { toast } from '../shared/Toast';
import '../shared/ConfirmDialog';

type RuleTab = 'pitch' | 'rest';

/**
 * Age-group filter for rest rules.
 * 'all' displays the catch-all rows (those with age_group = null/undefined).
 */
type AgeGroupFilter = 'U11' | 'U13' | 'U15' | 'U18' | 'U22' | 'all';

const AGE_GROUP_TABS: { value: AgeGroupFilter; label: string }[] = [
  { value: 'U11', label: '11U' },
  { value: 'U13', label: '13U' },
  { value: 'U15', label: '15U' },
  { value: 'U18', label: '18U' },
  { value: 'U22', label: '22U' },
  { value: 'all', label: 'All Ages' },
];

@customElement('rules-manager')
export class RulesManager extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private activeTab: RuleTab = 'pitch';

  @state()
  private seasons: Season[] = [];

  @state()
  private selectedSeasonId: string | null = null;

  @state()
  private pitchRules: PitchRule[] = [];

  @state()
  private restRules: RestRule[] = [];

  @state()
  private loading = true;

  // Pitch rule form
  @state()
  private showPitchForm = false;

  @state()
  private editingPitchRule: PitchRule | null = null;

  @state()
  private pitchFormAgeGroup = '';

  @state()
  private pitchFormMaxPitches = 75;

  @state()
  private pitchFormWarningPct = 80;

  // Rest rule form
  @state()
  private showRestForm = false;

  @state()
  private editingRestRule: RestRule | null = null;

  @state()
  private restFormAgeGroup: AgeGroupFilter = 'U11';

  @state()
  private restFormPitchMin = 0;

  @state()
  private restFormPitchMax = 0;

  @state()
  private restFormRestDays = 0;

  // Rest rule age-group sub-tab. Defaults to U11.
  @state()
  private restAgeTab: AgeGroupFilter = 'U11';

  @state()
  private saving = false;

  // Delete confirmation
  @state()
  private deleteConfirmOpen = false;

  @state()
  private deletingRule: { type: 'pitch' | 'rest'; rule: PitchRule | RestRule } | null = null;

  @state()
  private deleting = false;

  connectedCallback() {
    super.connectedCallback();
    this.loadSeasons();
    this.loadRules();
  }

  private async loadSeasons() {
    try {
      this.seasons = (await api.getSeasons()) || [];
      this.seasons.sort((a, b) => b.year - a.year);
    } catch (error) {
      console.error('Failed to load seasons:', error);
    }
  }

  private async loadRules() {
    this.loading = true;
    try {
      const [pitchRules, restRules] = await Promise.all([
        this.selectedSeasonId
          ? api.getSeasonPitchRules(this.selectedSeasonId)
          : api.getDefaultPitchRules(),
        this.selectedSeasonId
          ? api.getSeasonRestRules(this.selectedSeasonId)
          : api.getDefaultRestRules(),
      ]);
      this.pitchRules = pitchRules || [];
      this.restRules = restRules || [];
      // Sort pitch rules by age group
      this.pitchRules.sort((a, b) => a.age_group.localeCompare(b.age_group));
      // Sort rest rules by age_group (nulls last), then pitch_min
      this.restRules.sort((a, b) => {
        const ag = a.age_group || '';
        const bg = b.age_group || '';
        if (ag !== bg) {
          // Empty string (catch-all) sorts last
          if (!ag) return 1;
          if (!bg) return -1;
          return ag.localeCompare(bg);
        }
        return a.pitch_min - b.pitch_min;
      });
    } catch (error) {
      console.error('Failed to load rules:', error);
      toast.error('Failed to load rules');
    } finally {
      this.loading = false;
    }
  }

  private handleSeasonChange(seasonId: string) {
    this.selectedSeasonId = seasonId || null;
    this.loadRules();
  }

  // Pitch rule form methods
  private openPitchForm(rule?: PitchRule) {
    this.editingPitchRule = rule || null;
    this.pitchFormAgeGroup = rule?.age_group || '';
    this.pitchFormMaxPitches = rule?.max_pitches_game || 75;
    this.pitchFormWarningPct = rule?.warning_threshold_pct || 80;
    this.showPitchForm = true;
  }

  private closePitchForm() {
    this.showPitchForm = false;
    this.editingPitchRule = null;
    this.pitchFormAgeGroup = '';
    this.pitchFormMaxPitches = 75;
    this.pitchFormWarningPct = 80;
  }

  private async savePitchRule() {
    if (!this.pitchFormAgeGroup.trim()) {
      toast.error('Age group is required');
      return;
    }

    this.saving = true;
    try {
      if (this.editingPitchRule) {
        await api.updatePitchRule(this.editingPitchRule.id, {
          age_group: this.pitchFormAgeGroup.trim(),
          max_pitches_game: this.pitchFormMaxPitches,
          warning_threshold_pct: this.pitchFormWarningPct,
        });
        toast.success('Pitch rule updated');
      } else {
        await api.createPitchRule({
          season_id: this.selectedSeasonId || undefined,
          age_group: this.pitchFormAgeGroup.trim(),
          max_pitches_game: this.pitchFormMaxPitches,
          warning_threshold_pct: this.pitchFormWarningPct,
        });
        toast.success('Pitch rule created');
      }
      await this.loadRules();
      this.closePitchForm();
    } catch (error) {
      console.error('Failed to save pitch rule:', error);
      toast.error('Failed to save pitch rule');
    } finally {
      this.saving = false;
    }
  }

  // Rest rule form methods
  private openRestForm(rule?: RestRule) {
    this.editingRestRule = rule || null;
    if (rule) {
      // Normalize server "null" to 'all' for the form control.
      this.restFormAgeGroup = (rule.age_group as AgeGroupFilter) || 'all';
    } else {
      // Default the form age group to whichever sub-tab is active.
      this.restFormAgeGroup = this.restAgeTab;
    }
    this.restFormPitchMin = rule?.pitch_min || 0;
    this.restFormPitchMax = rule?.pitch_max || 0;
    this.restFormRestDays = rule?.rest_days_required || 0;
    this.showRestForm = true;
  }

  private closeRestForm() {
    this.showRestForm = false;
    this.editingRestRule = null;
    this.restFormAgeGroup = this.restAgeTab;
    this.restFormPitchMin = 0;
    this.restFormPitchMax = 0;
    this.restFormRestDays = 0;
  }

  private async saveRestRule() {
    if (this.restFormPitchMin < 0 || this.restFormPitchMax < this.restFormPitchMin) {
      toast.error('Invalid pitch range');
      return;
    }

    // 'all' maps to null (catch-all); anything else is a specific age string.
    const ageGroupValue = this.restFormAgeGroup === 'all' ? null : this.restFormAgeGroup;

    this.saving = true;
    try {
      if (this.editingRestRule) {
        await api.updateRestRule(this.editingRestRule.id, {
          age_group: ageGroupValue,
          pitch_min: this.restFormPitchMin,
          pitch_max: this.restFormPitchMax,
          rest_days_required: this.restFormRestDays,
        });
        toast.success('Rest rule updated');
      } else {
        await api.createRestRule({
          season_id: this.selectedSeasonId || undefined,
          age_group: ageGroupValue,
          pitch_min: this.restFormPitchMin,
          pitch_max: this.restFormPitchMax,
          rest_days_required: this.restFormRestDays,
        });
        toast.success('Rest rule created');
      }
      await this.loadRules();
      this.closeRestForm();
    } catch (error) {
      console.error('Failed to save rest rule:', error);
      toast.error('Failed to save rest rule');
    } finally {
      this.saving = false;
    }
  }

  /**
   * Filter rest rules to only those matching the currently selected age-group sub-tab.
   * 'all' shows rules whose age_group is null/undefined (catch-alls).
   */
  private filteredRestRules(): RestRule[] {
    if (this.restAgeTab === 'all') {
      return this.restRules.filter((r) => !r.age_group);
    }
    return this.restRules.filter((r) => r.age_group === this.restAgeTab);
  }

  private selectRestAgeTab(tab: AgeGroupFilter) {
    this.restAgeTab = tab;
    // If the form is open with no row being edited, realign the default age group.
    if (this.showRestForm && !this.editingRestRule) {
      this.restFormAgeGroup = tab;
    }
  }

  // Delete methods
  private confirmDelete(type: 'pitch' | 'rest', rule: PitchRule | RestRule) {
    this.deletingRule = { type, rule };
    this.deleteConfirmOpen = true;
  }

  private cancelDelete() {
    this.deleteConfirmOpen = false;
    this.deletingRule = null;
  }

  private async executeDelete() {
    if (!this.deletingRule) return;

    this.deleting = true;
    try {
      if (this.deletingRule.type === 'pitch') {
        await api.deletePitchRule(this.deletingRule.rule.id);
        toast.success('Pitch rule deleted');
      } else {
        await api.deleteRestRule(this.deletingRule.rule.id);
        toast.success('Rest rule deleted');
      }
      await this.loadRules();
      this.cancelDelete();
    } catch (error: any) {
      console.error('Failed to delete rule:', error);
      if (error.message?.includes('default')) {
        toast.error('Cannot delete default rules');
      } else {
        toast.error('Failed to delete rule');
      }
    } finally {
      this.deleting = false;
    }
  }

  private isDefaultRule(rule: PitchRule | RestRule): boolean {
    return !rule.season_id;
  }

  /**
   * Display the canonical "U11" age group as the user-facing "11U" label.
   * Stored value stays canonical so the pitch-limit engine can match it.
   */
  private formatAgeGroup(ageGroup: string | null | undefined): string {
    if (!ageGroup) return '';
    const m = /^U(\d+)$/.exec(ageGroup);
    return m ? `${m[1]}U` : ageGroup;
  }

  render() {
    return html`
      <div class="space-y-4">
        <div class="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <h3 class="text-lg font-semibold">Rules Management</h3>
          <div class="flex gap-2 items-center">
            <label class="label-text">Season:</label>
            <select
              class="select select-bordered"
              @change=${(e: Event) => this.handleSeasonChange((e.target as HTMLSelectElement).value)}
            >
              <option value="">Default Rules</option>
              ${this.seasons.map(
                (s) => html`<option value=${s.id}>${s.name} (${s.year})</option>`
              )}
            </select>
          </div>
        </div>

        <!-- Tabs -->
        <div role="tablist" class="tabs tabs-boxed">
          <button
            role="tab"
            class="tab ${this.activeTab === 'pitch' ? 'tab-active' : ''}"
            @click=${() => (this.activeTab = 'pitch')}
          >
            Pitch Limits
          </button>
          <button
            role="tab"
            class="tab ${this.activeTab === 'rest' ? 'tab-active' : ''}"
            @click=${() => (this.activeTab = 'rest')}
          >
            Rest Requirements
          </button>
        </div>

        ${this.loading
          ? html`
              <div class="space-y-2">
                ${[1, 2, 3].map(() => html`<div class="skeleton h-12 w-full"></div>`)}
              </div>
            `
          : this.activeTab === 'pitch'
            ? this.renderPitchRules()
            : this.renderRestRules()}

        <!-- Delete confirmation dialog -->
        <confirm-dialog
          .open=${this.deleteConfirmOpen}
          title="Delete Rule"
          .message=${this.deletingRule
            ? `Delete this ${this.deletingRule.type === 'pitch' ? 'pitch limit' : 'rest'} rule?`
            : ''}
          confirmText="Delete"
          .destructive=${true}
          .loading=${this.deleting}
          @confirm=${this.executeDelete}
          @cancel=${this.cancelDelete}
        ></confirm-dialog>
      </div>
    `;
  }

  private renderPitchRules() {
    return html`
      <div class="space-y-4">
        <div class="flex justify-between items-center">
          <p class="text-sm text-base-content/70">
            ${this.selectedSeasonId
              ? 'Season-specific pitch limits (overrides defaults)'
              : 'Default pitch limits by age group'}
          </p>
          <button class="btn btn-primary btn-sm" @click=${() => this.openPitchForm()}>
            Add Rule
          </button>
        </div>

        ${this.showPitchForm ? this.renderPitchForm() : nothing}

        ${this.pitchRules.length === 0
          ? html`
              <p class="text-center text-base-content/70 py-4">
                No pitch rules defined. Add one to set pitch limits.
              </p>
            `
          : html`
              <div class="overflow-x-auto">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Age Group</th>
                      <th class="text-right">Max Pitches/Game</th>
                      <th class="text-right">Warning Threshold</th>
                      <th>Type</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${this.pitchRules.map(
                      (rule) => html`
                        <tr class="hover">
                          <td class="font-semibold">${this.formatAgeGroup(rule.age_group)}</td>
                          <td class="text-right">${rule.max_pitches_game}</td>
                          <td class="text-right">${rule.warning_threshold_pct}%</td>
                          <td>
                            <span class="badge ${this.isDefaultRule(rule) ? 'badge-ghost' : 'badge-primary'}">
                              ${this.isDefaultRule(rule) ? 'Default' : 'Season'}
                            </span>
                          </td>
                          <td class="text-right">
                            <button
                              class="btn btn-ghost btn-xs"
                              @click=${() => this.openPitchForm(rule)}
                            >
                              Edit
                            </button>
                            <button
                              class="btn btn-ghost btn-xs text-error"
                              @click=${() => this.confirmDelete('pitch', rule)}
                              ?disabled=${this.isDefaultRule(rule) && !this.selectedSeasonId}
                              title=${this.isDefaultRule(rule) && !this.selectedSeasonId
                                ? 'Cannot delete default rules'
                                : 'Delete rule'}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      `
                    )}
                  </tbody>
                </table>
              </div>
            `}
      </div>
    `;
  }

  private renderPitchForm() {
    return html`
      <div class="card bg-base-200">
        <div class="card-body">
          <h4 class="card-title">
            ${this.editingPitchRule ? 'Edit Pitch Rule' : 'New Pitch Rule'}
          </h4>
          <div class="grid gap-4 md:grid-cols-3">
            <div class="form-control">
              <label class="label"><span class="label-text">Age Group *</span></label>
              <select
                class="select select-bordered"
                .value=${this.pitchFormAgeGroup}
                @change=${(e: Event) =>
                  (this.pitchFormAgeGroup = (e.target as HTMLSelectElement).value)}
              >
                <option value="" disabled ?selected=${!this.pitchFormAgeGroup}>Select age group</option>
                <option value="U11" ?selected=${this.pitchFormAgeGroup === 'U11'}>11U</option>
                <option value="U13" ?selected=${this.pitchFormAgeGroup === 'U13'}>13U</option>
                <option value="U15" ?selected=${this.pitchFormAgeGroup === 'U15'}>15U</option>
                <option value="U18" ?selected=${this.pitchFormAgeGroup === 'U18'}>18U</option>
                <option value="U22" ?selected=${this.pitchFormAgeGroup === 'U22'}>22U</option>
              </select>
              <label class="label"><span class="label-text-alt text-base-content/60">Stored canonically as U11/U13/U15/U18/U22.</span></label>
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Max Pitches/Game *</span></label>
              <input
                type="number"
                class="input input-bordered"
                min="1"
                max="200"
                .value=${this.pitchFormMaxPitches.toString()}
                @input=${(e: Event) =>
                  (this.pitchFormMaxPitches = parseInt(
                    (e.target as HTMLInputElement).value,
                    10
                  ))}
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Warning Threshold (%) *</span></label>
              <input
                type="number"
                class="input input-bordered"
                min="1"
                max="100"
                .value=${this.pitchFormWarningPct.toString()}
                @input=${(e: Event) =>
                  (this.pitchFormWarningPct = parseInt(
                    (e.target as HTMLInputElement).value,
                    10
                  ))}
              />
              <label class="label">
                <span class="label-text-alt">
                  Warning shown at ${Math.floor(
                    (this.pitchFormWarningPct / 100) * this.pitchFormMaxPitches
                  )} pitches
                </span>
              </label>
            </div>
          </div>
          <div class="card-actions justify-end mt-4">
            <button class="btn btn-ghost" @click=${this.closePitchForm}>Cancel</button>
            <button
              class="btn btn-primary"
              @click=${this.savePitchRule}
              ?disabled=${this.saving}
            >
              ${this.saving
                ? html`<span class="loading loading-spinner loading-sm"></span>`
                : 'Save'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderRestRules() {
    const visibleRules = this.filteredRestRules();
    return html`
      <div class="space-y-4">
        <div class="flex justify-between items-center">
          <p class="text-sm text-base-content/70">
            ${this.selectedSeasonId
              ? 'Season-specific rest requirements (overrides defaults)'
              : 'Default rest requirements based on pitch count'}
          </p>
          <button class="btn btn-primary btn-sm" @click=${() => this.openRestForm()}>
            Add Rule
          </button>
        </div>

        <!-- Age-group sub-tabs -->
        <div role="tablist" class="tabs tabs-bordered">
          ${AGE_GROUP_TABS.map(
            (tab) => html`
              <button
                role="tab"
                class="tab ${this.restAgeTab === tab.value ? 'tab-active' : ''}"
                @click=${() => this.selectRestAgeTab(tab.value)}
              >
                ${tab.label}
              </button>
            `
          )}
        </div>

        ${this.showRestForm ? this.renderRestForm() : nothing}

        ${visibleRules.length === 0
          ? html`
              <p class="text-center text-base-content/70 py-4">
                No rest rules defined for
                ${this.restAgeTab === 'all'
                  ? 'all ages'
                  : AGE_GROUP_TABS.find((t) => t.value === this.restAgeTab)?.label ||
                    this.restAgeTab}.
                Add one to set rest requirements.
              </p>
            `
          : html`
              <div class="overflow-x-auto">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Age Group</th>
                      <th>Pitch Range</th>
                      <th class="text-right">Rest Days Required</th>
                      <th>Type</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${visibleRules.map(
                      (rule) => html`
                        <tr class="hover">
                          <td class="font-semibold">
                            ${rule.age_group
                              ? rule.age_group.replace(/^U(\d+)$/, '$1U')
                              : 'All'}
                          </td>
                          <td class="font-semibold">${rule.pitch_min} - ${rule.pitch_max}</td>
                          <td class="text-right">
                            ${rule.rest_days_required} day${rule.rest_days_required !== 1
                              ? 's'
                              : ''}
                          </td>
                          <td>
                            <span class="badge ${this.isDefaultRule(rule) ? 'badge-ghost' : 'badge-primary'}">
                              ${this.isDefaultRule(rule) ? 'Default' : 'Season'}
                            </span>
                          </td>
                          <td class="text-right">
                            <button
                              class="btn btn-ghost btn-xs"
                              @click=${() => this.openRestForm(rule)}
                            >
                              Edit
                            </button>
                            <button
                              class="btn btn-ghost btn-xs text-error"
                              @click=${() => this.confirmDelete('rest', rule)}
                              ?disabled=${this.isDefaultRule(rule) && !this.selectedSeasonId}
                              title=${this.isDefaultRule(rule) && !this.selectedSeasonId
                                ? 'Cannot delete default rules'
                                : 'Delete rule'}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      `
                    )}
                  </tbody>
                </table>
              </div>
            `}
      </div>
    `;
  }

  private renderRestForm() {
    return html`
      <div class="card bg-base-200">
        <div class="card-body">
          <h4 class="card-title">
            ${this.editingRestRule ? 'Edit Rest Rule' : 'New Rest Rule'}
          </h4>
          <div class="grid gap-4 md:grid-cols-4">
            <div class="form-control">
              <label class="label"><span class="label-text">Age Group *</span></label>
              <select
                class="select select-bordered"
                .value=${this.restFormAgeGroup}
                @change=${(e: Event) =>
                  (this.restFormAgeGroup = (e.target as HTMLSelectElement)
                    .value as AgeGroupFilter)}
              >
                ${AGE_GROUP_TABS.map(
                  (tab) => html`
                    <option value=${tab.value} ?selected=${this.restFormAgeGroup === tab.value}>
                      ${tab.label}
                    </option>
                  `
                )}
              </select>
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Minimum Pitches *</span></label>
              <input
                type="number"
                class="input input-bordered"
                min="0"
                max="200"
                .value=${this.restFormPitchMin.toString()}
                @input=${(e: Event) =>
                  (this.restFormPitchMin = parseInt(
                    (e.target as HTMLInputElement).value,
                    10
                  ))}
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Maximum Pitches *</span></label>
              <input
                type="number"
                class="input input-bordered"
                min="0"
                max="200"
                .value=${this.restFormPitchMax.toString()}
                @input=${(e: Event) =>
                  (this.restFormPitchMax = parseInt(
                    (e.target as HTMLInputElement).value,
                    10
                  ))}
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Rest Days Required *</span></label>
              <input
                type="number"
                class="input input-bordered"
                min="0"
                max="10"
                .value=${this.restFormRestDays.toString()}
                @input=${(e: Event) =>
                  (this.restFormRestDays = parseInt(
                    (e.target as HTMLInputElement).value,
                    10
                  ))}
              />
              <label class="label">
                <span class="label-text-alt">
                  ${this.restFormRestDays === 0
                    ? 'No rest required'
                    : `Player available after ${this.restFormRestDays} calendar day${this.restFormRestDays !== 1 ? 's' : ''}`}
                </span>
              </label>
            </div>
          </div>
          <div class="card-actions justify-end mt-4">
            <button class="btn btn-ghost" @click=${this.closeRestForm}>Cancel</button>
            <button
              class="btn btn-primary"
              @click=${this.saveRestRule}
              ?disabled=${this.saving}
            >
              ${this.saving
                ? html`<span class="loading loading-spinner loading-sm"></span>`
                : 'Save'}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}
